import React, { useState, useEffect } from 'react';
import { Table, message, Typography, Space, Button, Spin, Tag, Input, Tooltip, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import dayjs from 'dayjs';
import AdminLayout from '../Layout/AdminLayout';
import { EyeOutlined, SearchOutlined, CheckCircleOutlined, ClockCircleOutlined, EditOutlined } from '@ant-design/icons';

const STATUS_CONFIG = {
    draft:     { color: 'default',    icon: <EditOutlined />,         label: 'Chưa hoàn thiện' },
    submitted: { color: 'processing', icon: <ClockCircleOutlined />,  label: 'Chờ thanh toán' },
    completed: { color: 'success',    icon: <CheckCircleOutlined />,  label: 'Hoàn thành' },
};

const STATUS_OPTIONS = [
    { value: '', label: 'Tất cả trạng thái' },
    ...Object.entries(STATUS_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label })),
];

const { Title } = Typography;
const { Search } = Input;

const StudentList = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const navigate = useNavigate();

    const fetchStudents = async (page = 1, pageSize = 10, search = '', status = '') => {
        try {
            setLoading(true);

            const params = { page, per_page: pageSize };
            if (search)  params.search = search;
            if (status)  params.status = status;

            const response = await axiosInstance.get('/api/v1/admin/student-list', { params });

            if (response.data.success) {
                const data = response?.data?.data?.map(item => ({
                    ...item,
                    full_name: item?.user?.name,
                    email: item?.user?.email,
                    student_phone_number: item?.user?.phone,
                    date_of_birth: item?.date_of_birth,
                    admission_area: item?.admission_area,
                    admission_group: item?.admission_group,
                    graduation_year: item?.graduation_year,
                    registered_at: item?.user?.created_at ? dayjs(item.user.created_at).format('DD/MM/YYYY HH:mm') : '-',
                    is_verified: item?.user?.is_verified ? dayjs(item?.user?.is_verified)?.format('DD/MM/YYYY HH:mm:ss') : '-',
                    is_paid: item?.payment?.is_paid,
                    status: item?.status || 'draft'
                }));

                setStudents(data);
                setPagination({
                    current: response.data.meta.current_page,
                    pageSize: response.data.meta.per_page,
                    total: response.data.meta.total,
                    hasMore: response.data.meta.has_more
                });
            }
        } catch (error) {
            console.error('Error fetching students:', error);
            if (error.response?.status === 401) {
                message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!');
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStudents(1, pagination.pageSize, searchText, statusFilter);
    }, [searchText, statusFilter]);

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleStatusFilter = (value) => {
        setStatusFilter(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const columns = [
        {
            title: 'Mã hồ sơ',
            dataIndex: 'student_code',
            key: 'student_code',
            sorter: (a, b) => a?.student_code?.localeCompare(b?.student_code)
        },
        {
            title: 'Họ và tên',
            dataIndex: 'full_name',
            key: 'full_name',
            sorter: (a, b) => a?.full_name?.localeCompare(b?.full_name)
        },
        {
            title: 'CCCD',
            dataIndex: 'identification',
            key: 'identification',
            sorter: (a, b) => a?.identification?.localeCompare(b?.identification)
        },
        {
            title: 'Ngày sinh',
            dataIndex: 'date_of_birth',
            key: 'date_of_birth',
            render: (date) => dayjs(date)?.isValid() ? dayjs(date)?.format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Số điện thoại',
            dataIndex: 'student_phone_number',
            key: 'student_phone_number',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => a?.email?.localeCompare(b?.email)
        },
        // {
        //     title: 'Khu vực',
        //     dataIndex: 'admission_area',
        //     key: 'admission_area',
        //     sorter: (a, b) => a?.admission_area?.localeCompare(b?.admission_area)
        // },
        // {
        //     title: 'Đối tượng',
        //     dataIndex: 'admission_group',
        //     key: 'admission_group',
        //     sorter: (a, b) => a?.admission_group?.localeCompare(b?.admission_group)
        // },
        // {
        //     title: 'Số nguyện vọng',
        //     dataIndex: 'admission_count',
        //     key: 'admission_count',
        //     sorter: (a, b) => a?.admission_count - b?.admission_count
        // },
        // {
        //     title: 'Năm tốt nghiệp',
        //     dataIndex: 'graduation_year',
        //     key: 'graduation_year',
        //     sorter: (a, b) => a?.graduation_year?.localeCompare(b?.graduation_year)
        // },
        {
            title: 'Ngày đăng ký',
            dataIndex: 'registered_at',
            key: 'registered_at',
            sorter: (a, b) => a?.registered_at?.localeCompare(b?.registered_at),
        },
        {
            title: 'Đã xác thực?',
            dataIndex: 'is_verified',
            key: 'is_verified',
            sorter: (a, b) => a?.is_verified?.localeCompare(b?.is_verified)
        },
        {
            title: 'Trạng thái hồ sơ',
            dataIndex: 'status',
            key: 'status',
            sorter: (a, b) => {
                const order = { draft: 0, submitted: 1, completed: 2 };
                return (order[a.status] ?? 0) - (order[b.status] ?? 0);
            },
            render: (status) => {
                const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
                return (
                    <Tooltip title={cfg.label}>
                        <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Hành động',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="link"
                        onClick={() => navigate(`/student-profile/${record.id}`)}
                    >
                        <EyeOutlined />
                    </Button>
                </Space>
            ),
        },
    ];

    const handleTableChange = (pag) => {
        fetchStudents(pag.current, pag.pageSize, searchText, statusFilter);
    };

    const totalKnown = pagination.total !== undefined && pagination.total !== null;

    const studentListContent = (
        loading && students.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        ) : (
            <>
                {/* Toolbar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Title level={3} style={{ margin: 0 }}>
                            Danh sách thí sinh đăng ký
                        </Title>
                        {totalKnown && (
                            <Tag color="blue" style={{ fontSize: 13, padding: '2px 10px' }}>
                                {pagination.total} thí sinh
                            </Tag>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Select
                            value={statusFilter}
                            onChange={handleStatusFilter}
                            options={STATUS_OPTIONS}
                            style={{ width: 200 }}
                            size="large"
                        />
                        <Search
                            placeholder="Nhập họ tên, điện thoại, email, cccd"
                            allowClear
                            enterButton={<SearchOutlined />}
                            size="large"
                            style={{ width: 360 }}
                            onSearch={handleSearch}
                        />
                    </div>
                </div>

                <Table
                    size="small"
                    columns={columns}
                    dataSource={students}
                    rowKey="id"
                    pagination={totalKnown ? {
                        ...pagination,
                        showTotal: (total, range) => `${range[0]}–${range[1]} / ${total} thí sinh`,
                    } : false}
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{ x: true }}
                />

                {!totalKnown && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 12 }}>
                        <Button
                            disabled={pagination.current <= 1}
                            onClick={() => fetchStudents(pagination.current - 1, pagination.pageSize, searchText, statusFilter)}
                        >
                            Trang trước
                        </Button>
                        <span>Trang {pagination.current}</span>
                        <Button
                            disabled={!pagination.hasMore}
                            onClick={() => fetchStudents(pagination.current + 1, pagination.pageSize, searchText, statusFilter)}
                        >
                            Trang sau
                        </Button>
                    </div>
                )}
            </>
        )
    );

    return (
        <AdminLayout title="Danh sách đăng ký">
            {studentListContent}
        </AdminLayout>
    );
};

export default StudentList;
